import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Função para aplicar tema da empresa
const applyCompanyTheme = (theme) => {
  const root = document.documentElement;
  
  if (theme && theme.primary) {
    // Set primary color from theme
    root.style.setProperty('--theme-primary', theme.primary);
    root.style.setProperty('--theme-primary-dark', theme.primary_dark || theme.primary);
    // Generate lighter variant
    const lighterColor = adjustColorBrightness(theme.primary, 30);
    root.style.setProperty('--theme-primary-light', lighterColor);
    root.style.setProperty('--theme-accent', theme.primary);
    root.style.setProperty('--theme-gradient-from', theme.primary_dark || theme.primary);
    root.style.setProperty('--theme-gradient-to', theme.primary);
    root.classList.add('themed');
  } else {
    // Reset to default theme
    root.style.removeProperty('--theme-primary');
    root.style.removeProperty('--theme-primary-dark');
    root.style.removeProperty('--theme-primary-light');
    root.style.removeProperty('--theme-accent');
    root.style.removeProperty('--theme-gradient-from');
    root.style.removeProperty('--theme-gradient-to');
    root.classList.remove('themed');
  }
};

// Helper function to adjust color brightness
const adjustColorBrightness = (hex, percent) => {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse hex
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  // Increase brightness
  r = Math.min(255, Math.round(r + (255 - r) * (percent / 100)));
  g = Math.min(255, Math.round(g + (255 - g) * (percent / 100)));
  b = Math.min(255, Math.round(b + (255 - b) * (percent / 100)));
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        // Apply saved theme
        if (parsedUser.company_theme) {
          applyCompanyTheme(parsedUser.company_theme);
        }
        // Verify token is still valid
        const response = await authApi.getMe();
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      } catch (err) {
        console.error('Error loading user:', err);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        applyCompanyTheme(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password, companySlug = null) => {
    try {
      setError(null);
      const response = await authApi.login(email, password, companySlug);
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      // Apply company theme
      if (userData.company_theme) {
        applyCompanyTheme(userData.company_theme);
      }
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Erro ao fazer login';
      setError(message);
      throw new Error(message);
    }
  };

  const lookupCompany = async (slug) => {
    try {
      const response = await authApi.companyLookup(slug);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Empresa não encontrada';
      throw new Error(message);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    applyCompanyTheme(null);
  };

  const isSuperAdmin = () => user?.role === 'super_admin';
  const isAdmin = () => user?.role === 'admin' || user?.role === 'super_admin';

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    lookupCompany,
    isSuperAdmin,
    isAdmin,
    refreshUser: loadUser,
    theme: user?.company_theme || null,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
